import { useEffect, useState } from "react";
import { Router, Loader2, Search, Stethoscope, ListChecks, Activity, Save, PlugZap, Play, Pause, UserX, History, FileCode, MessageCircle, Wrench, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, errMsg } from "@/api";

const fmtBytes = (n) => { const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let v = Number(n || 0); while (v >= 1024 && i < 4) { v /= 1024; i++; } return `${v.toFixed(i ? 1 : 0)} ${u[i]}`; };
const fmtBps = (n) => { const v = Number(n || 0); return v >= 1e6 ? `${(v / 1e6).toFixed(2)} Mbps` : `${(v / 1e3).toFixed(1)} Kbps`; };
const Box = ({ title, icon: Icon, children, testid }) => (
  <div className="panel" style={{ marginBottom: 14 }} data-testid={testid}>
    <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 10px", fontSize: 15 }}><Icon size={16} /> {title}</h3>
    {children}
  </div>
);

function ConfigBox({ q }) {
  const [f, setF] = useState({ host: "", port: 8728, username: "", password: "", use_ssl: false });
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/mikrotik/config", q).then((r) => { setCfg(r.data); setF((x) => ({ ...x, host: r.data.host, port: r.data.port, username: r.data.username, use_ssl: r.data.use_ssl, password: "" })); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy("save");
    try { const r = await api.put("/admin/mikrotik/config", { ...f, port: Number(f.port), password: f.password || null }, q); setCfg(r.data); setF((x) => ({ ...x, password: "" })); toast.success("Koneksi MikroTik disimpan"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const r = await api.post("/admin/mikrotik/test", {}, q); toast.success(`Terhubung: ${r.data.identity} (RouterOS ${r.data.version})`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); load(); }
  };
  const ok = cfg?.last_status === "terhubung";
  return (
    <Box title="Koneksi MikroTik" icon={Router} testid="mt-config">
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <label className="field-label">IP / Host<input className="text-input" value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="165.101.42.123" data-testid="mt-host" /></label>
        <label className="field-label">Port API<input className="text-input" type="number" value={f.port} onChange={(e) => setF({ ...f, port: e.target.value })} data-testid="mt-port" /></label>
        <label className="field-label">Username<input className="text-input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} autoComplete="off" data-testid="mt-username" /></label>
        <label className="field-label">Password<input className="text-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password"
          placeholder={cfg?.has_password ? "•••••• tersimpan (kosongkan bila tidak diganti)" : "Password MikroTik"} data-testid="mt-password" /></label>
        <label className="field-label">Jenis API
          <select className="text-input" value={f.use_ssl ? "ssl" : "api"} onChange={(e) => setF({ ...f, use_ssl: e.target.value === "ssl" })} data-testid="mt-ssl">
            <option value="api">API biasa</option><option value="ssl">API-SSL</option>
          </select></label>
      </div>
      <p className="muted-sm" data-testid="mt-port-hint">Router 165.101.42.123: API aktif di port 8729 (jenis "API biasa"). Port 8292 bukan port API.</p>
      <p className="muted-sm" data-testid="mt-status" style={{ color: ok ? "#15803d" : cfg?.last_status ? "#b91c1c" : undefined }}>
        Status: {cfg?.last_status ? `${cfg.last_status} — ${cfg.last_message || ""}` : "belum diuji"}{cfg?.router_identity ? ` · ${cfg.router_identity} (RouterOS ${cfg.router_version})` : ""}{cfg?.last_checked_at ? ` · ${new Date(cfg.last_checked_at).toLocaleString("id-ID")}` : ""}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn-primary sm" onClick={save} disabled={!!busy} data-testid="mt-save">{busy === "save" ? <Loader2 className="spin" size={14} /> : <Save size={14} />} Simpan</button>
        <button className="btn-ghost sm" onClick={test} disabled={!!busy || !cfg?.has_password} data-testid="mt-test">{busy === "test" ? <Loader2 className="spin" size={14} /> : <PlugZap size={14} />} Uji Koneksi</button>
      </div>
      <p className="muted-sm">Semua fitur MikroTik saat ini read-only: tidak ada perubahan apa pun di router.</p>
    </Box>
  );
}

const ALL = { mt_cek_voucher: true, mt_diagnosa: true, mt_perbaiki: true, mt_trafik: true, mt_pantau: true, mt_kick: true, mt_panduan: true };

function VoucherTools({ q, base = "/admin/mikrotik", perm = ALL }) {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    if (!code.trim()) return;
    setBusy(kind); setRes(null);
    try { const r = await api.post(`${base}/voucher/${kind}`, { code: code.trim() }, q); setRes({ kind, data: r.data }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const repair = async () => {
    const rp = res.data.repair;
    setBusy("repair");
    try { const r = await api.post(`${base}/voucher/repair`, { code: res.data.code, token: rp.token, ts: rp.ts, actions: rp.actions }, q); toast.success(r.data.message); setRes({ kind: "repaired", data: r.data }); }
    catch (e) { toast.error(errMsg(e)); setRes((x) => ({ ...x, repairError: errMsg(e) })); } finally { setBusy(""); }
  };
  const info = res?.kind === "check" ? res.data : res?.data?.info;
  const rp = res?.kind === "diagnose" ? res.data.repair : null;
  return (
    <Box title="Cek Voucher & Perbaiki Voucher Error" icon={Search} testid="mt-voucher-tools">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="text-input" style={{ flex: "1 1 180px" }} value={code} onChange={(e) => { setCode(e.target.value); setRes(null); }} placeholder="Kode voucher asli" data-testid="mt-code" />
        {perm.mt_cek_voucher && <button className="btn-primary sm" onClick={() => run("check")} disabled={!!busy} data-testid="mt-check">{busy === "check" ? <Loader2 className="spin" size={14} /> : <Search size={14} />} Cek</button>}
        {perm.mt_diagnosa && <button className="btn-ghost sm" onClick={() => run("diagnose")} disabled={!!busy} data-testid="mt-diagnose">{busy === "diagnose" ? <Loader2 className="spin" size={14} /> : <Stethoscope size={14} />} Diagnosa Error</button>}
      </div>
      {info && !info.found && <p className="error-text" data-testid="mt-not-found">{info.comparison?.status === "hilang_di_router" ? `Voucher ${info.code} tercatat di MITRAnet tetapi TIDAK ada di router.` : `Voucher ${info.code} tidak ada di router.`}</p>}
      {info?.comparison && (
        <p className="muted-sm" style={{ marginTop: 6, color: ["berbeda", "hilang_di_router"].includes(info.comparison.status) ? "#991b1b" : undefined }} data-testid="mt-compare">
          Perbandingan MITRAnet vs router (dibaca langsung dari router): <strong>{{ cocok: "cocok", berbeda: "BERBEDA", hilang_di_router: "HILANG di router", tanpa_catatan_lokal: "tidak ada catatan lokal untuk dibandingkan", tidak_ditemukan: "tidak ditemukan di keduanya" }[info.comparison.status] || info.comparison.status}</strong>
          {(info.comparison.diffs || []).map((d, i) => <span key={i}> · {d.field}: lokal "{d.local}" vs router "{d.router}"</span>)}
        </p>
      )}
      {info?.found && (
        <div className="muted-sm" style={{ marginTop: 8, display: "grid", gap: 2 }} data-testid="mt-voucher-info">
          <span>Status: <strong>{info.disabled ? "Nonaktif" : info.active.length ? "Sedang dipakai" : info.expired || info.uptime_exhausted || info.bytes_exhausted ? "Habis" : "Aktif"}</strong> · Profil: {info.profile}{!info.profile_exists && " (tidak ada di router)"}</span>
          <span>Masa berlaku: {info.expires_at ? new Date(info.expires_at).toLocaleString("id-ID") : "tidak tercatat"}{info.expired && " — KEDALUWARSA"}</span>
          <span>Pemakaian: {info.uptime || "0s"}{info.limit_uptime && ` dari ${info.limit_uptime}`} · Data {fmtBytes(info.bytes_in + info.bytes_out)}{info.limit_bytes_total ? ` dari ${fmtBytes(info.limit_bytes_total)}` : ""}</span>
          {info.mac_address && <span>Terkunci MAC: {info.mac_address}</span>}
          {info.comment && <span>Komentar: {info.comment}</span>}
        </div>
      )}
      {res?.kind === "diagnose" && (
        <div style={{ marginTop: 8 }} data-testid="mt-diagnose-result">
          <p className="muted-sm">Percobaan: {res.data.attempts}/{res.data.max_attempts} · Tindakan: {res.data.action_taken}</p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{res.data.causes.map((c, i) => <li key={i} data-testid={`mt-cause-${c.cause}`}>{c.message}</li>)}</ul>
          {rp?.repairable ? (
            <div style={{ marginTop: 8 }} data-testid="mt-repair-box">
              <p className="muted-sm">Dapat dipulihkan: {rp.action_labels.join(" + ")}. Kode, profil, durasi, comment, dan masa berlaku tetap. Maks 2 percobaan.</p>
              {perm.mt_perbaiki && <button className="btn-primary sm" onClick={repair} disabled={!!busy} data-testid="mt-repair">{busy === "repair" ? <Loader2 className="spin" size={14} /> : <Wrench size={14} />} Perbaiki Voucher</button>}
              {res.repairError && <p className="error-text" data-testid="mt-repair-error">{res.repairError}</p>}
            </div>
          ) : rp && <p className="muted-sm" style={{ color: "#92400e", marginTop: 6 }} data-testid="mt-repair-reason">Tidak dapat diperbaiki: {rp.reasons.join(" ")}</p>}
        </div>
      )}
      {res?.kind === "repaired" && (
        <p className="muted-sm" style={{ color: "#15803d", marginTop: 8 }} data-testid="mt-repair-done">{res.data.message} ({res.data.actions.join(", ")}; percobaan {res.data.attempts}/2)</p>
      )}
    </Box>
  );
}

function GeneratePlan({ q }) {
  const [codes, setCodes] = useState("");
  const [profile, setProfile] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mitraList, setMitraList] = useState([]);
  const [mitraId, setMitraId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [applied, setApplied] = useState(null);
  const loadProfiles = async () => { try { const r = await api.get("/admin/mikrotik/profiles", q); setProfiles(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const loadMitra = async () => { try { const r = await api.get("/admin/resellers", q); setMitraList(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const splitCodes = () => codes.split(/[\s,;]+/);
  const run = async () => {
    setBusy(true); setPlan(null); setApplied(null); setConfirming(false);
    try { const r = await api.post("/admin/mikrotik/voucher/generate-plan", { codes: splitCodes(), profile, mitra_id: mitraId || null }, q); setPlan(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/mikrotik/voucher/generate-apply", { codes: plan.ready, profile, mitra_id: mitraId || null, token: plan.token, ts: plan.ts, confirm: true }, q);
      setApplied(r.data); r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setConfirming(false); }
  };
  return (
    <Box title="Generate Voucher (rencana, read-only)" icon={ListChecks} testid="mt-generate">
      <p className="muted-sm">Tempel kode voucher asli dari e-Billing (satu per baris). Sistem memeriksa format, duplikat, dan kode yang sudah ada di router. Belum ada yang dikirim ke router.</p>
      <textarea className="text-input" rows={4} value={codes} onChange={(e) => setCodes(e.target.value)} placeholder={"KODE001\nKODE002"} data-testid="mt-gen-codes" />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
        <select className="text-input" style={{ flex: "1 1 160px" }} value={profile} onChange={(e) => setProfile(e.target.value)} onFocus={() => !profiles.length && loadProfiles()} data-testid="mt-gen-profile">
          <option value="">— Pilih profil hotspot —</option>
          {profiles.map((p) => <option key={p.name} value={p.name}>{p.name}{p.rate_limit && ` (${p.rate_limit})`}</option>)}
        </select>
        <select className="text-input" style={{ flex: "1 1 160px" }} value={mitraId} onChange={(e) => setMitraId(e.target.value)} onFocus={() => !mitraList.length && loadMitra()} data-testid="mt-gen-mitra">
          <option value="">— Pemilik voucher (Mitra), opsional —</option>
          {mitraList.map((m) => <option key={m.id} value={m.id}>{m.name}{m.comment_alias ? ` (Comment: ${m.comment_alias})` : ""}</option>)}
        </select>
        <button className="btn-primary sm" onClick={run} disabled={busy || !profile || !codes.trim()} data-testid="mt-gen-run">{busy ? <Loader2 className="spin" size={14} /> : <ListChecks size={14} />} Periksa Rencana</button>
      </div>
      {plan && (
        <div className="muted-sm" style={{ marginTop: 8 }} data-testid="mt-gen-result">
          <p>Siap dibuat: <strong>{plan.ready.length}</strong> · Sudah ada di router: {plan.already_on_router.length} · Duplikat: {plan.duplicate_in_input.length} · Format salah: {plan.invalid.length}</p>
          {plan.already_on_router.length > 0 && <p>Sudah ada: {plan.already_on_router.join(", ")}</p>}
          {plan.invalid.length > 0 && <p className="error-text">Format salah: {plan.invalid.join(", ")}</p>}
          <p style={{ color: "#92400e" }}>{plan.note}</p>
          {plan.comment_example && <p>Contoh Comment di router: <code>{plan.comment_example}</code></p>}
          {plan.token && plan.ready.length > 0 && !applied && (confirming
            ? <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span>Buat <strong>{plan.ready.length}</strong> voucher di router? Hasil dibaca ulang dari router.</span>
                <button className="btn-primary sm" onClick={apply} disabled={busy} data-testid="mt-gen-confirm">{busy ? <Loader2 className="spin" size={14} /> : <Check size={14} />} Ya, buat</button>
                <button className="btn-ghost sm" onClick={() => setConfirming(false)} disabled={busy}>Batal</button>
              </div>
            : <button className="btn-primary sm" onClick={() => setConfirming(true)} data-testid="mt-gen-apply"><Wrench size={14} /> Buat di Router</button>)}
        </div>
      )}
      {applied && (
        <div className="muted-sm" style={{ marginTop: 8 }} data-testid="mt-gen-applied">
          <p style={{ color: applied.ok ? "#15803d" : "#991b1b" }}><strong>{applied.message}</strong></p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{applied.results.map((x) => <li key={x.code}>{x.code}: {x.status}{x.error ? ` (${x.error})` : ""}</li>)}</ul>
        </div>
      )}
    </Box>
  );
}

function TrafficBox({ q, base = "/admin/mikrotik", perm = ALL, mitra = false }) {
  const [d, setD] = useState(null);
  const [iface, setIface] = useState("");
  const [busy, setBusy] = useState(false);
  const [find, setFind] = useState("");
  const [flag, setFlag] = useState("semua");
  const [kicking, setKicking] = useState("");
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const openKick = (a) => { setTarget(a); setReason(a.random_mac ? "mac_acak" : a.stuck ? "macet" : a.login_by === "trial" ? "trial" : ""); };
  const kick = async () => {
    const a = target;
    setKicking(a.id);
    try { const r = await api.post(`${base}/hotspot/kick`, { id: a.id, user: a.user, reason }, q); toast.success(r.data.message); setKickRev((n) => n + 1); setTarget(null); setD((x) => ({ ...x, hotspot_active: x.hotspot_active.filter((y) => y.id !== a.id), hotspot_active_count: x.hotspot_active_count - 1 })); }
    catch (e) { toast.error(errMsg(e)); setKickRev((n) => n + 1); } finally { setKicking(""); }
  };
  const [kickRev, setKickRev] = useState(0);
  const rows = d ? d.hotspot_active.filter((a) => (!find.trim() || (a.user || "").toLowerCase().includes(find.trim().toLowerCase()))
    && (flag === "semua" || (flag === "mac" && a.random_mac) || (flag === "tetap" && !a.random_mac) || (flag === "macet" && a.stuck))) : [];
  const [ifFind, setIfFind] = useState("");
  const ifaceMatch = d?.interfaces ? d.interfaces.filter((i) => !ifFind.trim() || i.name.toLowerCase().includes(ifFind.trim().toLowerCase())) : [];
  const ifaceOpts = d?.interfaces ? (ifaceMatch.some((i) => i.name === iface) ? ifaceMatch : [...d.interfaces.filter((i) => i.name === iface), ...ifaceMatch]) : [];
  const nMac = d ? d.hotspot_active.filter((a) => a.random_mac).length : 0;
  const nStuck = d ? d.hotspot_active.filter((a) => a.stuck).length : 0;
  const load = async (name = iface) => {
    setBusy(true);
    try { const r = await api.get(`${base}/traffic`, { params: { ...(q.params || {}), ...(name && !mitra ? { interface: name } : {}) } }); setD(r.data); setIface(r.data.interface || ""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Box title="Cek Trafik MikroTik" icon={Activity} testid="mt-traffic">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {d && !mitra && <input className="text-input" style={{ flex: "1 1 100%" }} value={ifFind} onChange={(e) => setIfFind(e.target.value)} placeholder={`Cari interface / nama pelanggan PPPoE (${d.interfaces.length})`} data-testid="mt-iface-search" />}
        {d && !mitra && <select className="text-input" style={{ flex: "1 1 160px" }} value={iface} onChange={(e) => load(e.target.value)} data-testid="mt-iface">{ifaceOpts.map((i) => <option key={i.name} value={i.name}>{i.name}{i.running ? "" : " (mati)"}</option>)}</select>}
        {d && !mitra && ifFind.trim() && <span className="muted-sm" style={{ flex: "1 1 100%" }} data-testid="mt-iface-count">{ifaceMatch.length} interface cocok — pilih dari daftar</span>}
        <button className="btn-primary sm" onClick={() => load()} disabled={busy} data-testid="mt-traffic-load">{busy ? <Loader2 className="spin" size={14} /> : <Activity size={14} />} {d ? "Segarkan" : "Cek Trafik"}</button>
      </div>
      {d && (
        <div style={{ marginTop: 8 }}>
          {!mitra && <p className="muted-sm" data-testid="mt-traffic-now">{d.interface_type === "pppoe-in" ? "Pelanggan PPPoE" : "Interface"} {d.interface}: Download <strong>{fmtBps(d.download_bps)}</strong> · Upload <strong>{fmtBps(d.upload_bps)}</strong> · CPU {d.router.cpu_load}% · Uptime {d.router.uptime}</p>}
          {mitra && !d.identity_linked && <p className="error-text" data-testid="mt-identity-missing">Identitas Mitra (alias e-Billing) belum terhubung, sehingga belum ada sesi yang dapat dicocokkan. Hubungi Admin Toko.</p>}
          <p className="muted-sm">{mitra ? "Sesi hotspot Anda yang online" : "Pelanggan hotspot aktif"}: <strong data-testid="mt-active-count">{d.hotspot_active_count}</strong></p>
          <LiveChart q={q} iface={mitra ? "Sesi Anda" : iface} base={base} mitra={mitra} enabled={perm.mt_pantau}>
            <span style={{ position: "relative", flex: "1 1 170px", display: "flex" }}>
              <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input className="text-input" style={{ paddingLeft: 28, width: "100%" }} value={find} onChange={(e) => setFind(e.target.value)} placeholder="Cari kode voucher / user" data-testid="mt-active-filter" />
            </span>
          </LiveChart>
          {find.trim() && <p className="muted-sm" data-testid="mt-filter-count">Ditemukan {rows.length} dari {d.hotspot_active.length} sesi</p>}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
            {[["semua", `Semua (${d.hotspot_active.length})`], ["mac", `MAC acak (${nMac})`], ["tetap", `MAC tetap (${d.hotspot_active.length - nMac})`], ["macet", `Macet TX/RX 0 (${nStuck})`]].map(([k, l]) => (
              <button key={k} className={flag === k ? "btn-primary sm" : "btn-ghost sm"} onClick={() => setFlag(k)} data-testid={`mt-flag-${k}`}>{l}</button>))}
            {perm.mt_panduan && <a className="btn-ghost sm" href="/panduan-mac" target="_blank" rel="noreferrer" data-testid="mt-panduan-link">Panduan matikan MAC acak ↗</a>}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="report-table" style={{ fontSize: 12, width: "100%" }} data-testid="mt-active-table">
              <thead><tr><th>User</th><th>IP</th><th>MAC</th><th>Sesi</th><th>Download</th><th>Upload</th><th></th></tr></thead>
              <tbody>{rows.map((a) => (
                <tr key={a.id || a.user} data-testid={`mt-active-row-${a.user}`}>
                  <td>{a.user}{a.login_by === "trial" && <span style={badge("#e0e7ff", "#3730a3")}>trial</span>}</td><td>{a.address}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{a.mac_address}{a.random_mac && <span style={badge("#ffedd5", "#9a3412")} data-testid={`mt-badge-mac-${a.user}`}>MAC acak</span>}</td>
                  <td>{a.uptime}{a.stuck && <span style={badge("#fee2e2", "#991b1b")} data-testid={`mt-badge-stuck-${a.user}`}>macet{a.idle_time ? ` · idle ${a.idle_time}` : ""}</span>}</td>
                  <td>{fmtBytes(a.bytes_out)}</td><td>{fmtBytes(a.bytes_in)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {perm.mt_panduan && <a className="btn-ghost sm" style={{ color: "#15803d" }} href={waPanduan(a.user)} target="_blank" rel="noreferrer" title="Kirim panduan MAC acak via WhatsApp" data-testid={`mt-wa-${a.user}`}><MessageCircle size={13} /> WA</a>}
                    {perm.mt_kick && <button className="btn-ghost sm" style={{ color: "#b91c1c", marginLeft: 4 }} disabled={!!kicking} onClick={() => openKick(a)} data-testid={`mt-kick-${a.user}`}>
                    <UserX size={13} /> Kick</button>}</td></tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
      <Dialog open={!!target} onOpenChange={(o) => !o && !kicking && setTarget(null)}>
        <DialogContent data-testid="mt-kick-dialog">
          <DialogHeader><DialogTitle>Putus sesi {target?.user}?</DialogTitle>
            <DialogDescription>{target?.address} · {target?.mac_address}. Voucher tetap ada dan bisa login lagi.</DialogDescription></DialogHeader>
          <div style={{ display: "grid", gap: 6 }}>
            <span className="field-label">Alasan Kick</span>
            {Object.entries(KICK_REASONS).map(([k, l]) => (
              <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
                <input type="radio" name="kick-reason" checked={reason === k} onChange={() => setReason(k)} data-testid={`mt-reason-${k}`} /> {l}
              </label>))}
          </div>
          <DialogFooter>
            <button className="btn-ghost sm" onClick={() => setTarget(null)} disabled={!!kicking} data-testid="mt-kick-cancel">Batal</button>
            <button className="btn-primary sm" style={{ background: "#b91c1c" }} onClick={kick} disabled={!reason || !!kicking} data-testid="mt-kick-confirm">
              {kicking ? <Loader2 className="spin" size={14} /> : <UserX size={14} />} Kick</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!mitra && <KickHistory q={q} rev={kickRev} />}
      {!mitra && <TrialScript />}
    </Box>
  );
}

const KICK_REASONS = { mac_acak: "MAC acak – HP tidak bisa login lagi", macet: "Sesi macet (TX/RX 0)", trial: "Penyalahgunaan trial 5 menit", permintaan: "Permintaan pelanggan" };
const waPanduan = (user) => `https://wa.me/?text=${encodeURIComponent(
  `Halo, pelanggan voucher ${user}. Agar voucher Wi-Fi tidak terputus/terkunci setelah HP mati, mohon matikan "MAC acak" di HP Anda. Panduan: ${window.location.origin}/panduan-mac`)}`;
const badge = (bg, fg) => ({ background: bg, color: fg, borderRadius: 999, padding: "1px 6px", fontSize: 10, marginLeft: 4, fontWeight: 600, whiteSpace: "nowrap" });

const TRIAL_SCRIPT = `:local mac $"mac-address"
:if ([:pick $mac 1 2] ~ "[26AEae]") do={
  :log warning ("MITRAnet: trial ditolak, MAC acak " . $mac)
  /ip hotspot active remove [find mac-address=$mac]
}`;

function TrialScript() {
  const [open, setOpen] = useState(false);
  const copy = () => navigator.clipboard.writeText(TRIAL_SCRIPT).then(() => toast.success("Skrip disalin"));
  return (
    <div style={{ marginTop: 14 }} data-testid="mt-trial-script">
      <button className="btn-ghost sm" onClick={() => setOpen(!open)} data-testid="mt-trial-script-toggle"><FileCode size={14} /> Skrip teknisi: tolak trial untuk MAC acak {open ? "▲" : "▼"}</button>
      {open && (
        <div className="muted-sm" style={{ marginTop: 6 }}>
          <p style={{ color: "#92400e" }}>Tidak dijalankan dari MITRAnet. Dipasang manual oleh teknisi lewat Winbox.</p>
          <ol style={{ paddingLeft: 18, margin: "4px 0", overflowWrap: "anywhere" }}>
            <li>IP → Hotspot → Server Profiles → buka profil server → tab Login: lihat <strong>Trial User Profile</strong>.</li>
            <li>IP → Hotspot → User Profiles → buka profil trial tersebut → tab Scripts → <strong>On Login</strong>.</li>
            <li><strong>Tambahkan</strong> skrip di bawah ke paling atas (jangan hapus skrip lama, misalnya dari Mikhmon), lalu OK.</li>
          </ol>
          <pre style={{ background: "#0f172a", color: "#e2e8f0", padding: 10, borderRadius: 8, fontSize: 12, overflowX: "auto" }} data-testid="mt-trial-script-code">{TRIAL_SCRIPT}</pre>
          <button className="btn-primary sm" onClick={copy} data-testid="mt-trial-script-copy">Salin skrip</button>
          <p>Hasil: HP dengan MAC acak langsung diputus saat mencoba trial, sehingga trial 5 menit tidak bisa diulang dengan MAC baru. Login voucher biasa tidak terpengaruh. Pelanggan diarahkan ke halaman panduan untuk mematikan MAC acak.</p>
        </div>
      )}
    </div>
  );
}

function KickHistory({ q, rev }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/mikrotik/kick-log", q).then((r) => setRows(r.data)).catch(() => setRows([])); }, [rev]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ marginTop: 14 }} data-testid="mt-kick-history">
      <h4 style={{ margin: "0 0 6px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}><History size={14} /> Riwayat Kick</h4>
      {rows && !rows.length && <p className="muted-sm" data-testid="mt-kick-history-empty">Belum ada sesi yang diputus.</p>}
      {rows?.length > 0 && (
        <div style={{ overflowX: "auto", maxHeight: 240, overflowY: "auto" }}>
          <table className="report-table" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr><th>Waktu</th><th>Diputus oleh</th><th>Sesi user</th><th>Alasan</th><th>Hasil</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={`${r.at}-${i}`} data-testid={`mt-kick-log-${i}`}><td style={{ whiteSpace: "nowrap" }}>{new Date(r.at).toLocaleString("id-ID")}</td><td>{r.by}</td><td>{r.user}</td><td data-testid={`mt-kick-log-reason-${i}`}>{r.reason}</td>
                <td style={{ color: r.ok ? "#15803d" : "#b91c1c" }}>{r.ok ? "Diputus" : "Gagal (sesi sudah berubah)"}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LIVE_MS = 5000;
const LIVE_MAX_MIN = 5;

function LiveChart({ q, iface, children, base = "/admin/mikrotik", mitra = false, enabled = true }) {
  const [on, setOn] = useState(false);
  const [pts, setPts] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => { setPts([]); }, [iface]);
  useEffect(() => {
    if (!on || !iface) return undefined;
    let stop = false;
    const started = Date.now();
    const tick = async () => {
      if (stop) return;
      if (Date.now() - started > LIVE_MAX_MIN * 60000) { setOn(false); toast.info(`Pantau langsung berhenti otomatis setelah ${LIVE_MAX_MIN} menit`); return; }
      try {
        const r = await api.get(`${base}/traffic/live`, mitra ? {} : { params: { ...(q.params || {}), interface: iface } });
        const t = new Date(r.data.at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        setPts((p) => [...p.slice(-35), { t, down: +(r.data.download_bps / 1e6).toFixed(2), up: +(r.data.upload_bps / 1e6).toFixed(2) }]);
        setErr("");
      } catch (e) { const m = errMsg(e); setErr(m); if (m.includes("offline")) { setOn(false); return; } }
      if (!stop) timer = setTimeout(tick, LIVE_MS);
    };
    let timer = setTimeout(tick, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [on, iface]); // eslint-disable-line react-hooks/exhaustive-deps
  const last = pts[pts.length - 1];
  return (
    <div style={{ margin: "10px 0" }} data-testid="mt-live">
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {enabled && <button className={on ? "btn-ghost sm" : "btn-primary sm"} onClick={() => setOn(!on)} data-testid="mt-live-toggle">
          {on ? <><Pause size={14} /> Hentikan</> : <><Play size={14} /> Pantau Langsung</>}
        </button>}
        {children}
      </div>
      <div style={{ marginTop: 4 }}>
        {on && <span className="muted-sm" style={{ display: "block" }} data-testid="mt-live-iface">Grafik: <strong>{iface}</strong></span>}
        <span className="muted-sm" data-testid="mt-live-now">
          {last ? <>↓ <strong style={{ color: "#2563eb" }}>{last.down} Mbps</strong> · ↑ <strong style={{ color: "#16a34a" }}>{last.up} Mbps</strong></> : `Diperbarui tiap ${LIVE_MS / 1000} detik, maks ${LIVE_MAX_MIN} menit`}
        </span>
      </div>
      {err && <p className="error-text" data-testid="mt-live-error">{err}</p>}
      {pts.length > 0 && (
        <div style={{ width: "100%", height: 200, marginTop: 6 }} data-testid="mt-live-chart">
          <ResponsiveContainer>
            <AreaChart data={pts} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={30} />
              <YAxis tick={{ fontSize: 10 }} unit="M" />
              <Tooltip formatter={(v, n) => [`${v} Mbps`, n === "down" ? "Download" : "Upload"]} />
              <Area type="monotone" dataKey="down" stroke="#2563eb" fill="#2563eb33" isAnimationActive={false} />
              <Area type="monotone" dataKey="up" stroke="#16a34a" fill="#16a34a33" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export const MitraMikrotikPanel = ({ slug }) => {
  const [acc, setAcc] = useState(null);
  const [testing, setTesting] = useState(false);
  const base = `/public/stores/${slug}/mikrotik`;
  useEffect(() => { api.get(`${base}/access`).then((r) => setAcc(r.data)).catch(() => setAcc(null)); }, [base]);
  if (!acc || !Object.values(acc.panels).some(Boolean)) return null;
  const p = acc.panels;
  const test = async () => {
    setTesting(true);
    try { const r = await api.post(`${base}/test`); toast.success(`${r.data.message}: ${r.data.identity}`); } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };
  return (
    <section style={{ marginTop: 18 }} data-testid="mitra-mikrotik-panel">
      <h2 className="section-heading">MikroTik</h2>
      {!acc.configured && <p className="error-text" data-testid="mitra-mt-not-configured">Koneksi MikroTik toko belum diatur Admin.</p>}
      {p.mt_uji_koneksi && <button className="btn-ghost sm" onClick={test} disabled={testing} style={{ marginBottom: 10 }} data-testid="mitra-mt-test">{testing ? <Loader2 className="spin" size={14} /> : <PlugZap size={14} />} Uji Koneksi</button>}
      {(p.mt_cek_voucher || p.mt_diagnosa) && <VoucherTools q={{}} base={base} perm={p} />}
      {p.mt_trafik && <TrafficBox q={{}} base={base} perm={p} mitra />}
      {!p.mt_trafik && p.mt_panduan && <a className="btn-ghost sm" href="/panduan-mac" target="_blank" rel="noreferrer" data-testid="mt-panduan-link">Panduan matikan MAC acak ↗</a>}
    </section>
  );
};

function MitraSessions({ q }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { setBusy(true); try { setD((await api.get("/admin/mikrotik/mitra-sessions", q)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <Box title="Ringkasan Sesi Online per Mitra" icon={ListChecks} testid="mt-mitra-sessions">
      <button className="btn-primary sm" onClick={load} disabled={busy} data-testid="mt-mitra-sessions-load">{busy ? <Loader2 className="spin" size={14} /> : <Activity size={14} />} {d ? "Segarkan" : "Tampilkan"}</button>
      {d && (
        <div style={{ marginTop: 8 }}>
          <p className="muted-sm" data-testid="mt-mitra-sessions-total">Online: <strong>{d.total_online}</strong> · terpetakan ke Mitra: <strong>{d.mapped_online}</strong> · belum terpetakan: <strong>{d.total_online - d.mapped_online - (d.ambiguous_online || 0)}</strong>{d.ambiguous_online > 0 && <> · <span style={badge("#fee2e2", "#991b1b")} data-testid="mt-ambiguous-count">ambigu (cocok &gt;1 Mitra): {d.ambiguous_online}</span></>}</p>
          <div style={{ overflowX: "auto" }}>
            <table className="report-table" style={{ fontSize: 12, width: "100%" }}>
              <thead><tr><th>Mitra</th><th>Identitas Comment</th><th>Online</th><th>+ Anak (Agen)</th></tr></thead>
              <tbody>{d.mitra.map((m) => (
                <tr key={m.id} data-testid={`mt-mitra-row-${m.id}`}><td>{m.name}</td>
                  <td>{(m.identities || []).join(" · ") || <span style={badge("#fef3c7", "#92400e")}>Belum ada</span>}</td>
                  <td>{m.online}</td><td>{m.online_with_children ?? "-"}</td></tr>))}</tbody>
            </table>
          </div>
          {(d.ambiguous || []).length > 0 && (
            <details open style={{ marginTop: 6 }} data-testid="mt-ambiguous">
              <summary className="muted-sm" style={{ cursor: "pointer", color: "#991b1b" }}>Comment cocok dengan lebih dari satu Mitra, tidak ditebak ({d.ambiguous.length}). Isi Alias Comment yang unik di data Mitra.</summary>
              <p className="muted-sm">{d.ambiguous.map((u) => `${u.identity} (${u.online})`).join(" · ")}</p>
            </details>
          )}
          {d.unmapped.length > 0 && (
            <details style={{ marginTop: 6 }} data-testid="mt-unmapped">
              <summary className="muted-sm" style={{ cursor: "pointer" }}>Identitas Comment di router yang belum terpetakan ({d.unmapped.length})</summary>
              <p className="muted-sm">{d.unmapped.map((u) => `${u.identity} (${u.online})`).join(" · ")}</p>
            </details>
          )}
        </div>
      )}
    </Box>
  );
}

export const MikrotikPanel = ({ storeId }) => {
  const q = storeId ? { params: { store_id: storeId } } : {};
  return (
    <div data-testid="mikrotik-panel">
      <ConfigBox q={q} />
      <VoucherTools q={q} />
      <GeneratePlan q={q} />
      <TrafficBox q={q} />
      <MitraSessions q={q} />
    </div>
  );
};
